import React, { useEffect, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import { cn } from '@/lib/utils';
import { FormControl, FormItem, FormLabel, FormMessage, FormField } from '@/components/ui/form';
import { Control, FieldPath, FieldValues } from 'react-hook-form';

interface PhoneInputProps {
  value?: string;
  onChange: (_value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Phone input component for admin forms.
 * Handles all edge cases: typing, paste, autofill, partial input.
 *
 * KEY BEHAVIOR:
 * - Accepts phone in any format: (555) 123-4567, 5551234567, +15551234567
 * - Displays formatted: +1 (555) ###-####
 * - Stores in form: +1XXXXXXXXXX (12 chars) when complete, empty string when incomplete
 * - This prevents Zod validation errors on partial input (fixes Sentry errors)
 * - User can type freely, validation only sees complete numbers or empty
 */
export const PhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  onChange,
  onBlur,
  placeholder = '+1 (555) 123-4567',
  className,
  disabled = false,
}) => {
  // Internal state for display - allows typing without triggering validation
  const [displayValue, setDisplayValue] = useState(() => {
    // Initialize from form value
    if (value && value.startsWith('+1') && value.length === 12) {
      return value.substring(2); // Strip +1 for display
    }
    return '';
  });

  // Sync display value when form value changes externally (e.g., editing existing record)
  useEffect(() => {
    if (value && value.startsWith('+1') && value.length === 12) {
      setDisplayValue(value.substring(2));
    } else if (!value) {
      setDisplayValue('');
    }
  }, [value]);

  const handleValueChange = (values: { value: string; formattedValue: string }) => {
    // values.value contains only digits (react-number-format strips formatting)
    const digitsOnly = values.value.replace(/\D/g, '');

    // Update display immediately (allows typing)
    setDisplayValue(digitsOnly);

    // Update form value ONLY when complete or empty
    if (digitsOnly.length === 0) {
      // Empty input - set form to empty string
      onChange('');
    } else if (digitsOnly.length === 10) {
      // Complete 10-digit phone - set form to +1XXXXXXXXXX
      onChange(`+1${digitsOnly}`);
    }
    // For 1-9 digits: update display but DON'T update form
    // This keeps form value as either valid phone or empty string
  };

  const handleBlur = () => {
    // On blur, if incomplete number, clear both display and form
    if (displayValue.length > 0 && displayValue.length < 10) {
      setDisplayValue('');
      onChange('');
    }
    onBlur?.();
  };

  return (
    <PatternFormat
      placeholder={placeholder}
      format="+1 (###) ###-####"
      value={displayValue}
      onValueChange={handleValueChange}
      onBlur={handleBlur}
      disabled={disabled}
      className={cn(
        // Use the exact same classes as the regular Input component
        'flex h-10 w-full text-text-light rounded-md border border-input border-disabled bg-white px-3 py-2 text-md ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-disabled',
        className,
      )}
    />
  );
};

interface PhoneFormFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const PhoneFormField = <T extends FieldValues>({
  control,
  name,
  label = 'Phone Number',
  placeholder = '+1 (555) 123-4567',
  className,
  disabled = false,
}: PhoneFormFieldProps<T>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel className="label">{label}</FormLabel>
          <FormControl>
            <PhoneInput
              value={field.value || ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={placeholder}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
